/**
 * TruePrice V1 - Validation Engine
 * 
 * Input validation and pre-calculation checks.
 * Source: Addendum E (P-GLOBAL-UI-01, P-GLOBAL-06)
 */

import {
  VerdictInputs,
  ValidationResult,
  ValidationError,
  DensityValidation,
  CalibrationResult,
  PreCalculationValidation,
  CareerStageKey,
  TierKey,
  MediumKey,
  CAREER_STAGES,
  validateCareerStage,
  findTierByDensity,
} from '../types';

import {
  calculateDensity,
  getDensityThresholds,
  checkMasteryOverride,
  checkMaterialException,
  checkSmallWorkLogic,
  calculateFloor,
} from './engine';

import { SIZE_THRESHOLDS } from '../constants';

// ============================================================================
// INPUT SANITIZATION (P-GLOBAL-UI-01)
// ============================================================================

/**
 * Validate all raw inputs before calculation
 * Source: Addendum E Section 2.4
 */
export function validateInputs(inputs: Partial<VerdictInputs>): ValidationResult {
  const errors: ValidationError[] = [];
  
  // Hours: must be > 0
  if (inputs.hours === undefined || inputs.hours === null) {
    errors.push({ field: 'hours', message: 'Time invested is required', code: 'required' });
  } else if (inputs.hours <= 0) {
    errors.push({ field: 'hours', message: 'Time invested must be greater than zero', code: 'zero' });
  }
  
  // Monthly Burn: must be > 0
  if (inputs.monthlyBurn === undefined || inputs.monthlyBurn === null) {
    errors.push({ field: 'monthlyBurn', message: 'Monthly expenses are required', code: 'required' });
  } else if (inputs.monthlyBurn <= 0) {
    errors.push({ field: 'monthlyBurn', message: 'Monthly expenses must be greater than zero', code: 'zero' });
  }
  
  // Monthly Capacity: must be > 0
  if (inputs.monthlyCapacity === undefined || inputs.monthlyCapacity === null) {
    errors.push({ field: 'monthlyCapacity', message: 'Monthly hours are required', code: 'required' });
  } else if (inputs.monthlyCapacity <= 0) {
    errors.push({ field: 'monthlyCapacity', message: 'Monthly hours must be greater than zero', code: 'zero' });
  }
  
  // Materials: must be >= 0
  if (inputs.materials === undefined || inputs.materials === null) {
    errors.push({ field: 'materials', message: 'Materials cost is required', code: 'required' });
  } else if (inputs.materials < 0) {
    errors.push({ field: 'materials', message: 'Materials cost cannot be negative', code: 'negative' });
  }
  
  // Width: must be > 0
  if (inputs.width === undefined || inputs.width === null) {
    errors.push({ field: 'width', message: 'Width is required', code: 'required' });
  } else if (inputs.width <= 0) {
    errors.push({ field: 'width', message: 'Width must be greater than zero', code: 'zero' });
  }
  
  // Height: must be > 0
  if (inputs.height === undefined || inputs.height === null) {
    errors.push({ field: 'height', message: 'Height is required', code: 'required' });
  } else if (inputs.height <= 0) {
    errors.push({ field: 'height', message: 'Height must be greater than zero', code: 'zero' });
  }
  
  // Gut Price: must be >= 0 if provided
  if (inputs.gutPrice !== undefined && inputs.gutPrice !== null && inputs.gutPrice < 0) {
    errors.push({ field: 'gutPrice', message: 'Your estimate cannot be negative', code: 'negative' });
  }
  
  // Medium: must be valid
  if (!inputs.medium) {
    errors.push({ field: 'medium', message: 'Medium is required', code: 'required' });
  }
  
  // Tier: must be valid
  if (!inputs.tier) {
    errors.push({ field: 'tier', message: 'Effort level is required', code: 'required' });
  }
  
  // Career Stage: must be valid
  if (!inputs.careerStage) {
    errors.push({ field: 'careerStage', message: 'Career stage is required', code: 'required' });
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

// ============================================================================
// DENSITY VALIDATION
// ============================================================================

/**
 * Validate density against tier benchmarks
 * Considers all exceptions (mastery, material, small work)
 */
export function validateDensity(
  hours: number,
  area: number,
  tier: TierKey,
  medium: MediumKey,
  careerStage: CareerStageKey,
  materials: number,
  ttcp: number,
  gutPrice?: number | null
): DensityValidation {
  const density = calculateDensity(hours, area);
  const thresholds = getDensityThresholds(tier, medium);

  const isUnderDensity = density < thresholds.densityMin;
  const isOverDensity = density > thresholds.densityMax;

  // Check exceptions
  const masteryOverride = checkMasteryOverride(careerStage);
  const materialException = checkMaterialException(materials, ttcp, gutPrice).exceeds;
  const smallWork = checkSmallWorkLogic(area);

  // Veto only applies if under density AND no exceptions apply
  const vetoApplies = isUnderDensity && !masteryOverride && !materialException;
  
  // For small works, over-density is informational only
  const effectiveOverDensity = smallWork ? false : isOverDensity;
  
  // Suggest appropriate tier if under density
  const suggestedTier = isUnderDensity ? findTierByDensity(density) : null;
  
  // Build reason string
  let vetoReason: string | null = null;
  if (vetoApplies) {
    vetoReason = `Your time investment (${density.toFixed(4)} h/sq in) is below the minimum for ${tier} (${thresholds.densityMin.toFixed(4)} h/sq in). Consider selecting ${suggestedTier} for accurate pricing.`;
  }
  
  return {
    valid: !vetoApplies,
    density,
    densityMin: thresholds.densityMin,
    densityMax: thresholds.densityMax,
    isUnderDensity,
    isOverDensity: effectiveOverDensity,
    suggestedTier,
    vetoApplies,
    vetoReason,
  };
}

// ============================================================================
// CALIBRATION GATE (P-GLOBAL-06)
// ============================================================================

/**
 * Check if career stage selection is valid given sales history
 * Source: Addendum E Section 2.3
 */
export function checkCalibrationGate(
  selectedStage: CareerStageKey,
  highestSale: number | null
): CalibrationResult {
  const validation = validateCareerStage(selectedStage, highestSale);
  const requiredSale = CAREER_STAGES[selectedStage].minimumHighestSale;
  
  let message: string | null = null;
  
  if (!validation.valid) {
    const saleDisplay = highestSale ? `$${(highestSale / 100).toLocaleString()}` : 'no recorded sales';
    const requiredDisplay = `$${(requiredSale / 100).toLocaleString()}`;
    
    message = `You selected "${CAREER_STAGES[selectedStage].label}" but your highest recorded sale is ${saleDisplay}. The market defines this stage as consistently selling above ${requiredDisplay}. We've adjusted your stage to "${CAREER_STAGES[validation.allowedStage].label}" to give you accurate market ceilings.`;
  }
  
  return {
    valid: validation.valid,
    requestedStage: selectedStage,
    allowedStage: validation.allowedStage,
    highestSale,
    requiredSale,
    message,
  };
}

// ============================================================================
// COMPLETE PRE-CALCULATION VALIDATION
// ============================================================================

/**
 * Run all validations before calculating a verdict
 * Returns combined results with warnings
 */
export function validateBeforeCalculation(
  inputs: Partial<VerdictInputs>
): PreCalculationValidation {
  const warnings: string[] = [];
  
  // Step 1: Input sanitization
  const inputValidation = validateInputs(inputs);
  
  if (!inputValidation.valid) {
    return {
      inputsValid: false,
      inputErrors: inputValidation.errors,
      densityValidation: null,
      calibrationResult: null,
      canProceed: false,
      warnings,
    };
  }
  
  // Cast to full inputs now that we know they're valid
  const fullInputs = inputs as VerdictInputs;
  const area = fullInputs.width * fullInputs.height;
  
  // Step 2: Calibration gate
  const calibrationResult = checkCalibrationGate(
    fullInputs.careerStage,
    fullInputs.highestSale
  );
  
  if (!calibrationResult.valid && calibrationResult.message) {
    warnings.push(calibrationResult.message);
  }
  
  // Step 3: Calculate floor for density validation
  const floor = calculateFloor(
    fullInputs.hours,
    fullInputs.monthlyBurn,
    fullInputs.monthlyCapacity,
    fullInputs.materials,
    fullInputs.tier
  );
  
  // Step 4: Density validation
  const densityValidation = validateDensity(
    fullInputs.hours,
    area,
    fullInputs.tier,
    fullInputs.medium,
    calibrationResult.valid ? fullInputs.careerStage : calibrationResult.allowedStage as CareerStageKey,
    fullInputs.materials,
    floor.ttcp
  );
  
  if (densityValidation.vetoReason) {
    warnings.push(densityValidation.vetoReason);
  }
  
  if (densityValidation.isOverDensity) {
    warnings.push(`Exceptional labor intensity detected. Your time investment exceeds typical ${fullInputs.tier} work.`);
  }
  
  // Small work notice
  if (area < SIZE_THRESHOLDS.SMALL_WORK) {
    warnings.push('Small work logic applied. Maximum density cap disabled for this piece size.');
  }
  
  return {
    inputsValid: true,
    inputErrors: [],
    densityValidation,
    calibrationResult,
    canProceed: true, // We allow proceeding even with warnings
    warnings,
  };
}

// ============================================================================
// FIELD-LEVEL VALIDATORS (for UI)
// ============================================================================

export function validateHours(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return 'Required';
  if (value <= 0) return 'Must be greater than zero';
  return null;
}

export function validateMonthlyBurn(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return 'Required';
  if (value <= 0) return 'Must be greater than zero';
  return null;
}

export function validateMonthlyCapacity(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return 'Required';
  if (value <= 0) return 'Must be greater than zero';
  if (value > 744) return 'Cannot exceed hours in a month'; // 31 days × 24 hours
  return null;
}

export function validateMaterials(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return 'Required';
  if (value < 0) return 'Cannot be negative';
  return null;
}

export function validateDimension(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return 'Required';
  if (value <= 0) return 'Must be greater than zero';
  if (value > 240) return 'Maximum 240 inches (20 feet)'; // Sanity check
  return null;
}

export function validateGutPrice(value: number | null | undefined): string | null {
  if (value === null || value === undefined) return 'Required';
  if (value <= 0) return 'Must be greater than zero';
  return null;
}
